import { certificates, type Certificate } from "@/data/certificates";
import {
  getAvailableBookingModes,
  getBookingAction,
  getBookingUrl,
  isBookableAction,
  isDualBookingAction,
  type BookingMode,
} from "@/data/certificateBookingActions";
import { certificatePathFor } from "@/data/certificateSlugs";

export type CompanyLang = "sv" | "en";

export const companyPaths = {
  hub: { sv: "/foretag", en: "/en/companies" },
  arbetsmiljo: { sv: "/foretag/arbetsmiljo", en: "/en/companies/occupational-health" },
  drogtestning: { sv: "/foretag/drogtestning", en: "/en/companies/drug-testing" },
  tjanster: { sv: "/foretag/tjanster", en: "/en/companies/services" },
  kontakt: { sv: "/foretag/kontakt", en: "/en/companies/contact" },
  bokaTid: { sv: "/foretag/boka-tid", en: "/en/companies/book" },
} as const;

export const companyDrugOptions = [
  { slug: "drogtest-forebyggande", title: { sv: "Drogtestning – förebyggande hälsovård", en: "Drug testing – preventive healthcare" } },
  { slug: "drogtest-nyanstallning", title: { sv: "Drogtestning vid nyanställning", en: "Drug testing at hiring" } },
  { slug: "drogtest-misstanke", title: { sv: "Drogtestning vid misstanke", en: "Drug testing on reasonable suspicion" } },
] as const;

export const isCompanyDrugService = (slug: string) =>
  slug === "drogtestning" || companyDrugOptions.some((option) => option.slug === slug);

export const companyInquiryHref = (lang: CompanyLang, service?: string, sample?: "saliv" | "urin") => {
  const params = new URLSearchParams();
  if (service) params.set("tjanst", service);
  if (sample) params.set("prov", sample);
  const query = params.toString();
  return query ? `${companyPaths.kontakt[lang]}?${query}` : companyPaths.kontakt[lang];
};

export const companyServiceLabel = (slug: string, lang: CompanyLang) => {
  const drug = companyDrugOptions.find((option) => option.slug === slug);
  if (drug) return drug.title[lang];
  if (slug === "flera-tjanster") {
    return lang === "sv" ? "Hjälp att välja tjänster" : "Help choosing services";
  }
  const cert = certificates.find((item) => item.slug === slug);
  return cert?.title[lang] ?? slug;
};

export const companyBookingHref = (lang: CompanyLang, slug?: string, mode?: BookingMode) => {
  const base = companyPaths.bokaTid[lang];
  if (!slug) return base;
  const params = new URLSearchParams({ intyg: slug });
  if (mode) params.set("besok", mode);
  return `${base}?${params.toString()}`;
};

export const isCompanyDirectBooking = (slug: string) => isBookableAction(getBookingAction(slug));

export const companyDirectBookingModes = (slug: string) => getAvailableBookingModes(getBookingAction(slug));

export const companyIsDualBooking = (slug: string) => isDualBookingAction(getBookingAction(slug));

export const companyDirectBookingUrl = (slug: string, mode?: BookingMode) =>
  getBookingUrl(getBookingAction(slug), mode);

export const companyDirectServices = () =>
  certificates.filter((cert) => isCompanyDirectBooking(cert.slug));

export const companyCertificateHref = (slug: string, lang: CompanyLang) =>
  certificatePathFor(slug, lang);

export const workEnvironmentSource =
  "https://www.av.se/arbetsmiljoarbete-och-inspektioner/publikationer/foreskrifter/afs-202315/";

export type WorkEnvironmentLandingSlug =
  | "afs-handintensivt"
  | "afs-vibrationer"
  | "nattarbete"
  | "afs-allergi-kemiska"
  | "afs-asbest"
  | "afs-bly"
  | "afs-klattring";

export const directWorkEnvironmentSlugs: WorkEnvironmentLandingSlug[] = [
  "afs-handintensivt",
  "afs-vibrationer",
  "nattarbete",
];

export const plannedWorkEnvironmentSlugs: WorkEnvironmentLandingSlug[] = [
  "afs-allergi-kemiska",
  "afs-asbest",
  "afs-bly",
  "afs-klattring",
];

export const workEnvironmentLanding: Record<
  WorkEnvironmentLandingSlug,
  { title: { sv: string; en: string }; price: { sv: string; en: string }; intro: { sv: string; en: string } }
> = {
  "afs-handintensivt": {
    title: {
      sv: "Medicinsk kontroll vid handintensivt arbete",
      en: "Medical examination for hand-intensive work",
    },
    price: { sv: "2 000 kr", en: "2,000 SEK" },
    intro: {
      sv: "Arbetar du med snabba, upprepade handledsrörelser som kräver kraft? Boka en medicinsk kontroll direkt på vår mottagning i Göteborg.",
      en: "Do you work with rapid, repetitive wrist movements that require force? Book a medical examination at our clinic in Gothenburg.",
    },
  },
  "afs-vibrationer": {
    title: {
      sv: "Medicinsk kontroll vid vibrationsexponering",
      en: "Medical examination for vibration exposure",
    },
    price: { sv: "2 000 kr", en: "2,000 SEK" },
    intro: {
      sv: "Använder du vibrerande verktyg i arbetet? Boka din vibrationskontroll direkt på mottagningen i Göteborg.",
      en: "Do you use vibrating tools at work? Book your vibration examination at our clinic in Gothenburg.",
    },
  },
  nattarbete: {
    title: {
      sv: "Medicinsk kontroll vid nattarbete",
      en: "Medical examination for night work",
    },
    price: { sv: "2 000 kr", en: "2,000 SEK" },
    intro: {
      sv: "Medicinsk kontroll vid nattarbete med fokus på hälsa, arbetstider, sömn och återhämtning. Boka direkt för ett fysiskt besök i Göteborg.",
      en: "Medical examination for night work focusing on health, working hours, sleep and recovery. Book an in-person visit in Gothenburg.",
    },
  },
  "afs-allergi-kemiska": {
    title: {
      sv: "Medicinsk kontroll vid allergiframkallande ämnen",
      en: "Medical examination for sensitising substances",
    },
    price: { sv: "Från 3 000 kr", en: "From 3,000 SEK" },
    intro: {
      sv: "Olika kemiska produkter ställer olika krav på medicinsk kontroll. Skicka en förfrågan så går vi igenom exponeringen och planerar rätt undersökning med er.",
      en: "Different chemical products place different requirements on medical examinations. Send an inquiry and we will review the exposure and plan the right examination with you.",
    },
  },
  "afs-asbest": {
    title: {
      sv: "Medicinsk kontroll vid asbest, kvarts och fibrer",
      en: "Medical examination for asbestos, silica and fibres",
    },
    price: { sv: "4 000 kr", en: "4,000 SEK" },
    intro: {
      sv: "Kontroller vid fibrosframkallande damm behöver planeras utifrån ämne och exponering. Skicka formuläret så samordnar vi underlag, undersökningar och besök med er.",
      en: "Examinations for fibrogenic dust need to be planned based on substance and exposure. Send the form and we will coordinate documentation, tests and visits with you.",
    },
  },
  "afs-bly": {
    title: {
      sv: "Medicinsk kontroll vid blyarbete",
      en: "Medical examination for lead exposure",
    },
    price: { sv: "4 000 kr", en: "4,000 SEK" },
    intro: {
      sv: "Vid blyexponering behöver läkarbedömning och provtagning samordnas. Skicka en förfrågan så planerar vi kontrollen och eventuell fortsatt provtagning med er.",
      en: "Lead exposure requires coordinated medical assessment and sampling. Send an inquiry and we will plan the examination and any follow-up sampling with you.",
    },
  },
  "afs-klattring": {
    title: {
      sv: "Medicinsk kontroll vid klättring med stor nivåskillnad",
      en: "Medical examination for climbing at height",
    },
    price: { sv: "Från 4 000 kr", en: "From 4,000 SEK" },
    intro: {
      sv: "Behöver ni en kontroll inför klättring i mast, stolpe, träd eller på stege? Skicka en förfrågan så går vi igenom arbetskraven och planerar undersökningen.",
      en: "Need an examination before climbing masts, poles, trees or ladders? Send an inquiry and we will review the job requirements and plan the examination.",
    },
  },
};

export const drugSources = {
  unilabs: "https://unilabs.se/v%C3%A5rdgivare/laboratorie-medicin/droganalyser",
  instructions: "https://anvisningar.se/Omraden/Droganalyser/",
  mro: "https://www.karolinska.se/vard/funktion/funktion-medicinsk-diagnostik-karolinska/vardgivare/universitetslaboratoriet/vara-verksamheter-kul/klinisk-farmakologi/droganalyslaboratoriet/",
  faq: "https://www.karolinska.se/vard/funktion/funktion-medicinsk-diagnostik-karolinska/vardgivare/universitetslaboratoriet/vara-verksamheter-kul/klinisk-farmakologi/droganalysaboratoriet-faq/",
  privacy: "https://www.imy.se/verksamhet/dataskydd/dataskydd-pa-olika-omraden/arbetsliv/kontroll-och-overvakning-av-anstallda/",
};

export const sampleTypeCards = [
  {
    key: "saliv" as const,
    title: { sv: "Salivtest", en: "Saliva test" },
    description: {
      sv: "Prov från munvätska med ett särskilt provtagningskit.",
      en: "Oral fluid sample collected with a dedicated sampling kit.",
    },
  },
  {
    key: "urin" as const,
    title: { sv: "Urinprov", en: "Urine test" },
    description: {
      sv: "Urinprov enligt de provtagningsrutiner som gäller för ärendet.",
      en: "Urine sample according to the sampling routines that apply to the case.",
    },
  },
];

export type CompanyCatalogGroupId =
  | "arbetsmiljo"
  | "korkort"
  | "sjofart"
  | "alkohol"
  | "arbete"
  | "resor"
  | "sport";

export const companyCatalogGroups: {
  id: CompanyCatalogGroupId;
  title: { sv: string; en: string };
  match: (cert: Certificate) => boolean;
}[] = [
  {
    id: "arbetsmiljo",
    title: { sv: "Arbetsmiljö", en: "Occupational health" },
    match: (c) => c.category === "afs" || c.slug === "nattarbete",
  },
  {
    id: "korkort",
    title: { sv: "Körkort", en: "Driving licence" },
    match: (c) => c.category === "korkort",
  },
  {
    id: "sjofart",
    title: { sv: "Sjöfart & offshore", en: "Seafaring & offshore" },
    match: (c) => c.category === "sjofolk",
  },
  {
    id: "alkohol",
    title: { sv: "Alkohol & droger", en: "Alcohol & drugs" },
    match: (c) => c.category === "alkohol",
  },
  {
    id: "arbete",
    title: { sv: "Arbete & studier", en: "Work & studies" },
    match: (c) => c.category === "arbete" && c.slug !== "nattarbete",
  },
  {
    id: "resor",
    title: { sv: "Resor & försäkring", en: "Travel & insurance" },
    match: (c) => c.category === "resor" || c.category === "forsakring",
  },
  {
    id: "sport",
    title: { sv: "Sport & övriga intyg", en: "Sport & other certificates" },
    match: (c) =>
      c.category === "sport" || c.category === "adoption" || c.category === "ovriga",
  },
];

export const certificatesForCompanyGroup = (id: CompanyCatalogGroupId) => {
  const group = companyCatalogGroups.find((g) => g.id === id);
  if (!group) return [];
  return certificates.filter(group.match);
};
